<template>
  <houseHeader />
  <div class="combined-page">

    <div class="body-container">
      <div class="category-container">
        <div v-for="(item, index) in categoryItems" :key="index" class="category-row"
          :class="{ 'selected': selectedIndex === index }">
          <!-- 分类标签（纵向） -->
          <div class="category-label" @click="selectItem(index)">
            <i :class="item.icon"></i>
            <span>{{ item.label }}</span>
          </div>

          <!-- 值区域（横向滚动） -->
          <div class="values-scroll-container">
            <div class="values-container">
              <div v-for="(value, valueIndex) in item.values" :key="valueIndex" class="value-item"
                :class="{ 'selected-value': selectedValues[item.key] === valueIndex }"
                @click.stop="selectValue(item.key, valueIndex)">
                {{ value }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 别墅图片区域 -->
      <div class="villa-list-container">
        <!-- 无数据提示 -->
        <div v-if="noData" class="no-data-tip">
          <i class="fas fa-inbox"></i>
          <p>暂无相关房型数据</p>
        </div>

        <!-- 有数据时显示列表 -->
        <div v-else>
          <div class="villa-grid">
            <div v-for="villa in products" :key="villa.houseId" class="villa-item" @click="gotoDetail(villa.houseId)">
              <div class="villa-image-container">
                <!-- 图片加载前显示loading -->
                 <!-- !villa.imageLoaded -->
                <div v-if="true" class="simple-loader"></div>
                <img 
                  :src="villa.houseImageUrl" 
                  :alt="villa.houseName" 
                  class="villa-image" 
                  loading="lazy"
                  @load="onImageLoad(villa)"
                  @error="onImageError(villa)"
                />
              </div>
              <div class="name-container">
                <div class="villa-name-dot">凯尚设计</div>
                <h2 class="villa-name">{{ villa.houseName }}</h2>
              </div>
            </div>
          </div>

          <!-- 加载更多按钮和状态 -->
          <div class="load-more-container">
            <!-- 加载中动画（三个点跳动效果） -->
            <div v-if="loading" class="dot-loading">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>

            <!-- 加载更多按钮 -->
            <button v-else-if="hasMore" @click="loadMore" class="load-more-btn">
              加载更多
            </button>

            <!-- 已加载完毕提示 -->
            <div v-else class="no-more-tip">
              <span>已加载全部数据</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <HouseBottom></HouseBottom>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import HouseBottom from "@/views/house/bottom/houseBottom.vue";
import houseHeader from '@/views/house/bottom/houseHeader.vue';

const { proxy } = getCurrentInstance();
const router = useRouter();
const selectedIndex = ref(null);
const currentPage = ref(1);
const pageSize = ref(10);
const totalPage = ref(0);
const products = ref([]);
const hasMore = ref(true);
const loading = ref(false);
const noData = ref(false);

// 使用对象存储选中的分类值
const selectedValues = ref({
  category: 0,
  style: 0,
  floor: 0
});

// 分类数据 - 使用更语义化的键名
const categoryItems = ref([
  {
    icon: 'fas fa-layer-group',
    label: '分类',
    key: 'category',
    values: ['别墅设计']
  },
  {
    icon: 'fas fa-filter',
    label: '风格',
    key: 'style',
    values: ['全部', '中式', '新中式', '合院', '简欧风', '现代风']
  },
  {
    icon: 'fas fa-filter',
    label: '层数',
    key: 'floor',
    values: ['全部', '一层别墅', '二层别墅', '三层别墅', '双拼别墅', '火爆户型', '本月新增']
  },
]);

// 计算当前选中的查询参数
const queryParams = computed(() => ({
  houseType: selectedValues.value.floor,
  page: currentPage.value,
  pageSize: pageSize.value,
  style: selectedValues.value.style
}));

// 选择分类项
const selectItem = (index) => {
  selectedIndex.value = index;
};

// 选择值项
const selectValue = (categoryKey, valueIndex) => {
  selectedValues.value[categoryKey] = valueIndex;
  queryHouseByCategory();
};

// 图片加载完成处理
const onImageLoad = (villa) => {
  villa.imageLoaded = true;
};

// 图片加载失败处理
const onImageError = (villa) => {
  villa.imageLoaded = true; // 即使加载失败也隐藏loading
  // 可以设置默认图片
  // villa.houseImageUrl = '/path/to/default-image.jpg';
};

// 查询房屋数据
const queryHouseByCategory = async (page = 1) => {
  try {
    loading.value = true;
    noData.value = false;
    currentPage.value = page;
    const response = await proxy.$tools.request('/house/newQueryHouse', {
      reqBody: { ...queryParams.value, page }
    });

    const { houseList = [], totalPage: total } = response.data.rspBody;

    // 为每个villa添加imageLoaded属性
    const processedHouseList = houseList.map(house => ({
      ...house,
      imageLoaded: false
    }));

    if (page === 1) {
      products.value = processedHouseList;
      noData.value = processedHouseList.length === 0;
    } else {
      products.value = [...products.value, ...processedHouseList];
    }

    totalPage.value = total;
    hasMore.value = currentPage.value < totalPage.value;

    // 检查是否无更多数据
    if (!hasMore.value && products.value.length === 0) {
      noData.value = true;
    }

  } catch (error) {
    console.error('房屋查询失败:', error);
    noData.value = true;
  } finally {
    loading.value = false;
  }
};

// 加载更多
const loadMore = () => {
  if (hasMore.value && !loading.value) {
    queryHouseByCategory(currentPage.value + 1);
  }
};

const gotoDetail = (id) => {
  // 保存当前状态到 localStorage
  localStorage.setItem('houseCategoryState', JSON.stringify({
    products: products.value,
    currentPage: currentPage.value,
    totalPage: totalPage.value,
    selectedValues: selectedValues.value,
    selectedIndex: selectedIndex.value
  }));
  
  router.push({
    name: "HouseDetail",
    params: { houseId: id }
  });
};

onMounted(() => {
  // 尝试从 localStorage 恢复状态
  const savedState = localStorage.getItem('houseCategoryState');
  if (savedState) {
    const state = JSON.parse(savedState);
    // 恢复时也需要为每个villa添加imageLoaded属性
    products.value = state.products.map(product => ({
      ...product,
      imageLoaded: false
    }));
    currentPage.value = state.currentPage;
    totalPage.value = state.totalPage;
    selectedValues.value = state.selectedValues;
    selectedIndex.value = state.selectedIndex;
    localStorage.removeItem('houseCategoryState'); // 恢复后清除
  } else {
    // 没有保存的状态，正常加载
    queryHouseByCategory();
  }
});
</script>

<style scoped>
/* 添加全局样式重置 */
:global(body),
:global(html) {
  margin: 0;
  padding: 0;
}

.combined-page {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #2c3e50;
  padding-bottom: 68px;
  background: #f5f5f5;
  /* margin-top: 20px; */
}

.search-bar {
  display: flex;
  /* padding: 12px 16px; */
  background: #fff;
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
  margin-bottom: 18px;
}

.search-bar input {
  flex: 1;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 14px;
}

.search-bar button {
  padding: 10px 20px;
  border: none;
  background: #007bff;
  color: white;
  border-radius: 8px;
  margin-left: 8px;
  font-weight: 500;
}

.promo-banner {
  position: relative;
  background: url('@/assets/beijing.jpg') center/cover no-repeat;
  width: 100%;
  height: 80px;
  margin-top: -20px;
}

.promo-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
}

.promo-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.promo-content h4 {
  color: white;
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.8);
  /* margin-bottom: 0.5px; */
  padding-top: 15px;
  font-size: 1.3rem;
  font-weight: bold;
}


.body-container {
  width: 100%;
  /* background: #fff; */
  background: #ededed;
}

.category-container {
  /* padding: 10px 15px; */
  /* margin-top: 10px; */
  background: #fff;
  /* border-radius: 8px; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.category-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  /* cursor: pointer; */
  position: relative;
}

.category-row:not(:last-child) {
  border-bottom: 1px solid #eee;
}

.category-label {
  display: flex;
  gap: 6px;
  align-items: center;
  min-width: 60px;
  color: #a1a1a8;
  font-size: 0.9rem;
  /* padding: 4px 0; */

}

.category-label i {
  font-size: 0.9rem;
}

.values-scroll-container {
  flex: 1;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.values-container {
  display: flex;
  flex-wrap: nowrap;
  margin-left: 20px;
  gap: 12px;
  min-width: max-content;
}

.value-item {
  /* padding: 6px 12px; */
  /* border-radius: 16px; */
  padding-right: 12px;
  font-size: 0.9rem;
  color: #595d69;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;
  font-weight: 500;
  cursor: pointer;
}

.value-item.selected-value {
  color: #ff6022;
  font-weight: bold;
  /* background: #fff7f0; */
  position: relative;
}

.value-item.selected-value::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 30%;
  width: 10px;
  height: 2px;
  background-color: #ff6022;
}

.values-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.values-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.values-scroll-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.values-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

.villa-list-container {
  padding: 20px 15px;
  margin: 0 auto;
}

.villa-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.villa-item {
  background: #ededed;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.villa-item:hover {
  transform: translateY(-5px);
}

.villa-image-container {
  position: relative;
  width: 100%;
  padding-top: 75%;
  overflow: hidden;
  cursor: pointer;
}

.villa-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /**object-fit: cover;*/
  object-fit: scale-down; 
}

.name-container {
  width: 100%;
  padding: 8px 10px;
  background: #fff;
  text-align: start;
}

.villa-name-dot {
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-bottom: 4px;
  color: #a1a1a8;
  font-size: 0.75rem;
  cursor: pointer;
}

.villa-name-dot:hover {
  color: #d1d1d8;
}

.villa-name-dot::before {
  display: block;
  content: "";
  border-radius: 50%;
  height: 6px;
  width: 6px;
  margin-right: 5px;
  background: rgba(33, 99, 232, 0.75)
}

.villa-name {
  margin: 0;
  overflow: hidden;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(89, 93, 105);
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.villa-name:hover {
  color: rgb(1, 6, 16);
}

.load-more-container {
  margin-top: 30px;
  text-align: center;
  margin-bottom: 10px;
}

.loading-spinner {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #666;
  padding: 12px 24px;
}

.loading-spinner i {
  font-size: 20px;
  color: #007bff;
}

.load-more-btn {
  background-color: #333;
  color: white;
  border: none;
  padding: 8px 50px;
  border-radius: 30px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.load-more-btn:hover {
  background-color: #555;
  transform: scale(1.05);
}

.no-more-tip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #666;
  padding: 10px 20px;
  background: #f8f8f8;
  border-radius: 20px;
  font-size: 0.95rem;
}

.no-more-tip i {
  color: #52c41a;
}

.no-data-tip {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.no-data-tip i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #ccc;
}

.no-data-tip p {
  font-size: 16px;
}

.dot-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  height: 40px;
}

.dot-loading .dot {
  width: 8px;
  height: 8px;
  background-color: #333;
  border-radius: 50%;
  animation: dotFlashing 1s infinite linear;
}

.dot-loading .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot-loading .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotFlashing {
  0% {
    opacity: 0.5;
    transform: scale(0.8);
  }

  50% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0.5;
    transform: scale(0.8);
  }
}

.simple-loader {
  position: absolute;
  top: 10px;
  width: 100%;
  height: 100%;
  /* background: #f0f0f0 url('@/assets/loading.gif') center center no-repeat; / */
  /* 或者使用纯CSS动画 */
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.simple-loader::after {
  content: "";
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin1 1s linear infinite;
}
@keyframes spin1 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@media (min-width: 769px) {
  .category-container {
    width: calc(100% - 300px);
    padding-left: 150px;
    padding-right: 150px;
  }

  .villa-list-container {
    width: calc(100% - 300px);
    padding-left: 150px;
    padding-right: 150px;
  }


}

@media (max-width: 768px) {
  .promo-banner {
    height: 60px;
  }
  .promo-content h4 {
  padding-top: 15px;
  font-size: 16px;

}

  .category-container {
    width: calc(100% - 30px);
    padding-left: 20px;
    padding-right: 10px;
  }

  .category-row {
    padding: 8px 0;
  }

  .category-label {
    gap: 3px;
    font-size: 0.935rem;
  }

  .category-label i {
    font-size: 0.935rem;
  }

  .values-container {
    margin-left: 0px;
    gap: 4px;
  }

  .value-item {
    /* padding: 5px 10px; */
    font-size: 0.935rem;
  }

  .villa-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .villa-name {
    font-size: 0.7rem;
    padding: 8px 5px;
  }

  .no-data-tip {
    padding: 40px 15px;
  }

  .no-data-tip i {
    font-size: 36px;
  }

  .no-data-tip p {
    font-size: 14px;
  }

  .name-container {
    padding: 0;
    height: 30px;
  }

  .villa-name-dot {
    margin-top: 1px;
    margin-bottom: 2px;
    padding-left: 2px;
    font-size: 0.6rem;
  }


  .villa-name {
    margin-top: -8px;
    margin-bottom: 2px;
  }

  @media (max-width: 480px) {
    .villa-grid {
      gap: 10px;
    }

    .category-container {
      margin-left: 0;
      margin-right: 0;
    }
  }
}
</style>
